import { useState, useEffect, useReducer, useMemo, useCallback } from 'react'
import { notification } from 'tea-component'
import { throttle } from 'lodash-es'
import moment from 'moment'

// import useSubscribeFilterValue from '@/pages/subscribe/hooks/useSubscribeFilterValue'
import { useLocal } from '@/pages/dashboard/utils/customHook'
import { timeFormatter } from '@/pages/dashboard/utils/utils'
import { FORM_MAP, RESET, TIMETYPEOBJ, CONTROL } from '../_config/constant'

import useLockValue, { getCacheOptions } from './useLockValue'
import { FilterProps } from '../types'

const useFilter = (props: FilterProps) => {
  const { getLocalText } = useLocal()
  const {
    value,
    loading,
    defaultValue,
    setShowBubble,
    onConfirm,
    onInnerChange,
    onReset,
  } = props
  const defaultFormState: any = useMemo(() => {
    return {
      [FORM_MAP.RANGE_TIME]: [moment().subtract(1, 'day').format('YYYY-MM-DD'), moment().subtract(1, 'day').format('YYYY-MM-DD')],
      [FORM_MAP.RANGE_INIT]: false,
      [FORM_MAP.SINGLE_TIME]: moment().subtract(1, 'day').format('YYYY-MM-DD'),
      [FORM_MAP.CONTRAST_DAY]: moment().subtract(7, 'day').format('YYYY-MM-DD'),
      [FORM_MAP.DAY_MONTH]: {
        value: ['1', '30'],
        type: 'Day'
      },
      [FORM_MAP.RANGE_TIME_DIFF]: false,
      [FORM_MAP.RANGE_TIME_TYPE]: 'Day',
      [FORM_MAP.TRACKING_DAY]: '2',
      [FORM_MAP.NETWORK_TYPE]: ['255'],
      [FORM_MAP.MEDIA_CHANNEL]: ['255'],
      [FORM_MAP.IS_INIT]: false,
      ...(defaultValue ?? {})
    }
  }, [])
  // 初始化表单
  const init = (initState) => {
    return {
      ...initState,
    }
  }
  const reducer = (state, action) => {
    const { type, payload } = action

    switch (type) {
      case CONTROL: {
        return {
          ...state,
          ...payload,
        }
      }
      case RESET: {
        return init(defaultFormState)
      }
      default: {
        const formKeys = Object.values(FORM_MAP)
        if (type || formKeys.includes(type)) {
          return {
            ...state,
            [type]: payload
          }
        }
        throw new Error()
      }
    }
  }
  const [formState, dispatch] = useReducer(reducer, {
    ...defaultFormState,
    ...value
  }, init)
  const { lockCacheValue, setLockValue } = useLockValue(getCacheOptions())
  const [isLockFilter, setIsLockFilter] = useState(lockCacheValue?.isLockFilter ?? false)
  const [confirmFlag, setConfirmFlag] = useState(false)
  // const { setSubscribeFilterValue } = useSubscribeFilterValue()

  // #region  子筛选器 change 事件
  const onDayMonthChange = ({ value, type }) => {
    dispatch({
      type: FORM_MAP.DAY_MONTH,
      payload: {
        value,
        type
      },
    })
  }

  const onRangeTimeChange = (value, type, key = null, callback) => {
    const timeType = TIMETYPEOBJ[type]
    if (timeType === 'Month') {
      value = [
        moment(value[0]).startOf("month").format('YYYY-MM-DD'),
        moment(value[1]).endOf("month").format('YYYY-MM-DD')
      ]
    } else {
      value = [
        moment(value[0]).format('YYYY-MM-DD'),
        moment(value[1]).format('YYYY-MM-DD')
      ]
    }
    const diff = moment(value[1]).diff(moment(value[0]), 'days') + 1;
    callback?.(value, timeType, key)

    dispatch({
      type: key ?? FORM_MAP.RANGE_TIME,
      payload: value,
    })
    dispatch({
      type: (key ? `${key}_` : '') + FORM_MAP.RANGE_TIME_DIFF,
      payload: diff > 14
    })
    dispatch({
      type: (key ? `${key}_` : '') + FORM_MAP.RANGE_TIME_TYPE,
      payload: timeType
    })
    dispatch({
      type: (key ? `${key}_` : '') + FORM_MAP.RANGE_INIT,
      payload: true
    })
  }

  const onSingleTimeChange = (value, key = null) => {
    dispatch({
      type: key ?? FORM_MAP.SINGLE_TIME,
      payload: timeFormatter(value),
    })
  }

  const onContrastDayChange = (value, key = null) => {
    dispatch({
      type: key ?? FORM_MAP.CONTRAST_DAY,
      payload: timeFormatter(value),
    })
  }

  const onZoneChange = (value, key = null) => {
    dispatch({
      type: key ?? FORM_MAP.ZONE,
      payload: value,
    })
  }

  const onRegionChange = (value, key = null) => {
    dispatch({
      type: key ?? FORM_MAP.REGION,
      payload: value,
    })
  }

  const onSystemChange = (value, key = null) => {
    dispatch({
      type: key ?? FORM_MAP.SYSTEM,
      payload: value,
    })
  }
  const onLaguageChange = (value, key = null) => {
    dispatch({
      type: key ?? FORM_MAP.LANGUAGE,
      payload: value,
    })
    // 每次修改language筛选器的时候, 此处为了同步 language 和 lang 的 value, 避免下钻时无法继承language筛选器
    dispatch({
      type: "lang",
      payload: value
    })
  }
  const onChannelChange = (value, key = null) => {
    dispatch({
      type: key ?? FORM_MAP.CHANNEL,
      payload: value,
    })
  }

  const onTrackingDayChange = (value, key) => {
    dispatch({
      type: key ?? FORM_MAP.TRACKING_DAY,
      payload: value,
    })
  }

  const onNetworkTypeChange = (value, key = null) => {
    dispatch({
      type: key ?? FORM_MAP.NETWORK_TYPE,
      payload: value,
    })
  }

  const onMediaChannelChange = (value, key = null) => {
    dispatch({
      type: key ?? FORM_MAP.MEDIA_CHANNEL,
      payload: value,
    })
  }

  const onFilterChange = (key, value) => {
    dispatch({
      type: key,
      payload: value,
    })
  }

  const onCustomizedInputChange = (key, value) => {
    dispatch({
      type: key,
      payload: value,
    })
  }

  const onSwitchDatePickerChange = (value) => {
    dispatch({
      type: FORM_MAP.SWITCH_DATE_PICKER,
      payload: value,
    })
  }

  const onLockChange = (flag) => {
   // 此处为了同步 language 和 lang 的 value, 避免下钻时无法继承language筛选器
    if(value?.language) {
      value.lang = value?.language
    }
    notification.success({
      description: getLocalText(flag ? 'filter_lock_success' : 'filter_lock_fail'),
      className: 'filter-lock-alert'
    })
    setIsLockFilter(flag)
    setLockValue({
      ...value,
      isLockFilter: flag
    })
  }
  // #endregion

  // 提交
  const handleConfirm = useCallback(throttle(() => {
    if (loading) return
    setShowBubble(false)
    // 延迟 confirm，避免子筛选器 change 时间发生在 confirm 之后，无法取到最新值
    setTimeout(() => {
      setConfirmFlag(true)
    }, 500)
  }, 2000, { leading: true }), [loading])

  // 重置
  const handleReset = useCallback(throttle(() => {
    if (loading) return
    const newFormState = { ...(defaultFormState || {}), isLockFilter }
    setLockValue(newFormState)
    dispatch({ type: RESET })
    onReset?.(newFormState)
  }, 2000, { leading: true }), [loading, defaultFormState, isLockFilter])

  useEffect(() => {
    if (!confirmFlag) return

    const newFormState = { ...(formState || {}), isLockFilter }

    setLockValue(newFormState)
    onConfirm?.(newFormState)
    // setSubscribeFilterValue?.(newFormState)
    setConfirmFlag(false)
  }, [confirmFlag, formState])

  // 受控
  useEffect(() => {
    const newFormState = { ...formState }
    Object.keys(value ?? {})?.forEach(key => {
      newFormState[key] = value?.[key]
       // 首次进入页面时, 此处为了同步 language 和 lang 的 value, 避免下钻时无法继承language筛选器
       if (key === 'language' || key === 'lang') {
        newFormState.lang = value?.language
      }
    })
    dispatch({
      type: CONTROL,
      payload: newFormState,
    })
  }, [JSON.stringify(value ?? {})])


  // 筛选器内部状态变化事件
  useEffect(() => {
    onInnerChange?.(formState)
  }, [JSON.stringify(formState)])

  return {
    loading,
    formState,
    isLockFilter,
    dispatch,
    onLockChange,
    onFilterChange,
    onDayMonthChange,
    onRangeTimeChange,
    onSingleTimeChange,
    onContrastDayChange,
    onZoneChange,
    onRegionChange,
    onSystemChange,
    onLaguageChange,
    onChannelChange,
    onSwitchDatePickerChange,
    onTrackingDayChange,
    onNetworkTypeChange,
    onMediaChannelChange,
    onCustomizedInputChange,
    handleConfirm,
    handleReset,
  }
}

export default useFilter
